import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { TOYAMA_LOGO_BASE64 } from './toyama-logo';

const LOGO_BASE64 = TOYAMA_LOGO_BASE64;
const COMPANY_NAME = 'TOYAMA CONTROLS & SYSTEMS';
const COLORS = {
	ink: '#10223A',
	teal: '#226A73',
	lime: '#97D700',
	paper: '#F8F7F7',
	line: '#DBDBDB',
	muted: '#64748b'
};

type PdfMakeLike = {
	vfs?: Record<string, string>;
	createPdf: (docDefinition: TDocumentDefinitions) => { download: (filename?: string) => void };
};

type LogicData = {
	project: { name: string };
	unit: { name: string };
	zones: Array<{ id: number; name: string }>;
	rooms: Array<{ id: number; zone?: number; name: string }>;
	loads: Array<{ id: number; room?: number; name?: string; load_type?: string }>;
	rcus: Array<{ id: number }>;
	dinModules: Array<{ id: number; module_type?: string; address?: number }>;
	channels?: Array<{ object_id?: number; load?: number; channel_number?: number }>;
	allMappedChannels?: Array<{ object_id?: number; load_id?: number; channel_number?: number }>;
	keypads: Array<{ id: number; address?: number }>;
	keypadKeys: Array<{ id: number; keypad: number; key_number?: number; key_index?: number; name?: string | null }>;
	keyActions: Array<{ key: number; event_type?: string; action_type?: string; action_target?: number }>;
	scenes: Array<{ id: number; name?: string }>;
	sceneLoads: Array<{ scene: number; load?: number; load_id?: number }>;
	savedLedMappings?: Array<{ x_address: number; x_key: number; x_state: number; led_targets: Array<{ address: number; key: number; state: number }> }>;
};

function sceneLoadTarget(sl: { load?: number; load_id?: number }): number | undefined {
	return sl.load ?? sl.load_id;
}

async function loadPdfMake(): Promise<PdfMakeLike> {
	if (typeof window !== 'undefined' && (window as { pdfMake?: PdfMakeLike }).pdfMake) {
		return (window as { pdfMake: PdfMakeLike }).pdfMake;
	}
	const pdfMakeModule = await import('pdfmake/build/pdfmake');
	const fontsModule = await import('pdfmake/build/vfs_fonts');
	const pdfMake = (pdfMakeModule.default || pdfMakeModule) as PdfMakeLike;
	const fonts = (fontsModule.default || fontsModule) as { pdfMake?: { vfs?: Record<string, string> }; vfs?: Record<string, string> };
	pdfMake.vfs = fonts.pdfMake?.vfs || fonts.vfs || pdfMake.vfs;
	if (typeof window !== 'undefined') (window as { pdfMake?: PdfMakeLike }).pdfMake = pdfMake;
	return pdfMake;
}

function formatDate(): string {
	const date = new Date();
	return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function titleCase(value: unknown): string {
	if (!value) return '-';
	return String(value).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (m) => m.toUpperCase());
}

function eventStateLabel(eventType?: string): string {
	if (eventType === 'off') return 'Off';
	if (eventType === 'hold') return 'Hold';
	if (eventType === 'double_tap') return 'Double Tap';
	return 'On';
}

function ledStateLabel(state: unknown): string {
	return Number(state) === 1 ? 'On' : 'Off';
}

function createMaps(data: LogicData) {
	const channels = data.allMappedChannels ?? data.channels ?? [];
	return {
		zonesById: new Map(data.zones.map((item) => [item.id, item])),
		roomsById: new Map(data.rooms.map((item) => [item.id, item])),
		loadsById: new Map(data.loads.map((item) => [item.id, item])),
		channelsByLoadId: new Map(
			channels
				.filter((item) => (item as { load_id?: number; load?: number }).load_id || (item as { load_id?: number; load?: number }).load)
				.map((item) => [((item as { load_id?: number; load?: number }).load_id ?? (item as { load?: number }).load) as number, item])
		),
		keypadsById: new Map(data.keypads.map((item) => [item.id, item])),
		keypadsByAddress: new Map(data.keypads.map((item) => [item.address, item])),
		keypadKeysById: new Map(data.keypadKeys.map((item) => [item.id, item])),
		scenesById: new Map(data.scenes.map((item) => [item.id, item])),
		rcusById: new Map(data.rcus.map((item) => [item.id, item])),
		dinById: new Map(data.dinModules.map((item) => [item.id, item]))
	};
}

function getRoomTitle(room: { zone?: number; name: string }, maps: ReturnType<typeof createMaps>) {
	const zone = room.zone ? maps.zonesById.get(room.zone) : undefined;
	return zone ? `${room.name}  /  ${zone.name}` : room.name;
}
function getKeyForKeypadButton(keypadId: number, buttonNumber: number, maps: ReturnType<typeof createMaps>) {
	for (const key of maps.keypadKeysById.values()) {
		const number = key.key_number ?? key.key_index;
		if (key.keypad === keypadId && number === buttonNumber) return key;
	}
	return null;
}
function getKeyByAddressAndNumber(address: number, buttonNumber: number, maps: ReturnType<typeof createMaps>) {
	const keypad = maps.keypadsByAddress.get(address);
	if (!keypad) return null;
	return getKeyForKeypadButton(keypad.id, buttonNumber, maps);
}
function getButtonLabel(key: { keypad: number; key_number?: number; key_index?: number; name?: string | null } | null, maps: ReturnType<typeof createMaps>) {
	if (!key) return 'Unknown Button';
	const keypad = maps.keypadsById.get(key.keypad);
	const address = keypad?.address ?? '?';
	const buttonNumber = key.key_number ?? key.key_index ?? '?';
	const name = key.name?.trim() || `Button ${buttonNumber}`;
	return `${name} (KP ${address} / B${buttonNumber})`;
}
function getLedButtonLabel(address: number, buttonNumber: number, maps: ReturnType<typeof createMaps>) {
	const key = getKeyByAddressAndNumber(address, buttonNumber, maps);
	return key ? getButtonLabel(key, maps) : `Keypad ${address} / B${buttonNumber}`;
}

function getControllerLabel(channel: { object_id?: number; channel_number?: number } | undefined, maps: ReturnType<typeof createMaps>) {
	if (!channel) return '-';
	const rcu = maps.rcusById.get(channel.object_id ?? -1);
	if (rcu) return `RCU / Ch ${channel.channel_number}`;
	const din = maps.dinById.get(channel.object_id ?? -1);
	if (din) return `${titleCase(din.module_type || 'DIN')} DIN ${din.address ?? din.id} / Ch ${channel.channel_number}`;
	return `Controller ${channel.object_id} / Ch ${channel.channel_number}`;
}

function getActionLoads(action: { action_type?: string; action_target?: number }, data: LogicData, maps: ReturnType<typeof createMaps>) {
	if (action.action_type === 'load') {
		const load = action.action_target ? maps.loadsById.get(action.action_target) : undefined;
		return load ? [load] : [];
	}
	if (action.action_type === 'scene') {
		return data.sceneLoads.filter((sl) => sl.scene === action.action_target).map((sl) => maps.loadsById.get(sceneLoadTarget(sl) ?? -1)).filter(Boolean);
	}
	return [];
}

function getActionControlLabel(action: { action_type?: string }, data: LogicData, maps: ReturnType<typeof createMaps>) {
	const loads = getActionLoads(action, data, maps);
	if (!loads.length) return action.action_type === 'scene' ? 'Scene' : titleCase(action.action_type);
	if (action.action_type === 'scene') return loads.map((load) => `- ${load?.name}`).join('\n');
	return loads.map((load) => load?.name).join(', ');
}

function collectLoadControllers(data: LogicData, maps: ReturnType<typeof createMaps>) {
	const byLoadId = new Map<number, string[]>();
	for (const action of data.keyActions) {
		const key = maps.keypadKeysById.get(action.key);
		if (!key) continue;
		const buttonLabel = getButtonLabel(key, maps);
		const state = eventStateLabel(action.event_type);
		const loads = getActionLoads(action, data, maps);
		for (const load of loads) {
			const loadId = load?.id;
			if (!loadId) continue;
			const existing = byLoadId.get(loadId) || [];
			existing.push(`${buttonLabel} -> ${state}`);
			byLoadId.set(loadId, existing);
		}
	}
	return byLoadId;
}

function buildLoadChartSections(data: LogicData, maps: ReturnType<typeof createMaps>) {
	const controllersByLoad = collectLoadControllers(data, maps);
	return data.rooms
		.slice()
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((room) => {
			const loads = data.loads
				.filter((load) => load.room === room.id)
				.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
				.map((load, index) => {
					const channel = maps.channelsByLoadId.get(load.id);
					const controllers = controllersByLoad.get(load.id) || [];
					return [String(index + 1), load.name || `Load ${load.id}`, titleCase(load.load_type), getControllerLabel(channel as { object_id?: number; channel_number?: number }, maps), controllers.join('\n') || '-'];
				});
			return { room, rows: loads };
		})
		.filter((section) => section.rows.length > 0);
}

function buildLogicSections(data: LogicData, maps: ReturnType<typeof createMaps>) {
	const sections = new Map<number, string[][]>();
	for (const action of data.keyActions) {
		const key = maps.keypadKeysById.get(action.key);
		if (!key) continue;
		const loads = getActionLoads(action, data, maps);
		if (!loads.length) continue;
		const roomIds = [...new Set(loads.map((load) => load?.room).filter(Boolean))] as number[];
		for (const roomId of roomIds) {
			const roomLoads = loads.filter((load) => load?.room === roomId);
			const rows = sections.get(roomId) || [];
			rows.push([
				String(rows.length + 1),
				getButtonLabel(key, maps),
				eventStateLabel(action.event_type),
				roomLoads.map((load) => load?.name || `Load ${load?.id}`).join('\n'),
				[...new Set(roomLoads.map((load) => titleCase(load?.load_type)))].join('\n'),
				roomLoads.map((load) => getControllerLabel(maps.channelsByLoadId.get(load?.id || -1) as { object_id?: number; channel_number?: number }, maps)).join('\n')
			]);
			sections.set(roomId, rows);
		}
	}
	return data.rooms.slice().sort((a, b) => a.name.localeCompare(b.name)).map((room) => ({ room, rows: sections.get(room.id) || [] })).filter((s) => s.rows.length > 0);
}

function buildLedLogicRows(data: LogicData, maps: ReturnType<typeof createMaps>) {
	return (data.savedLedMappings ?? []).map((mapping, index) => {
		const controls: string[] = [];
		const triggerKey = getKeyByAddressAndNumber(mapping.x_address, mapping.x_key, maps);
		if (triggerKey) {
			for (const action of data.keyActions) {
				if (action.key !== triggerKey.id) continue;
				if (eventStateLabel(action.event_type) !== ledStateLabel(mapping.x_state)) continue;
				controls.push(getActionControlLabel(action, data, maps));
			}
		}
		const targetLabels = mapping.led_targets.map((target) => `${getLedButtonLabel(target.address, target.key, maps)} -> ${ledStateLabel(target.state)}`);
		return [String(index + 1), getLedButtonLabel(mapping.x_address, mapping.x_key, maps), ledStateLabel(mapping.x_state), controls.join('\n') || '-', targetLabels.join('\n') || '-'];
	});
}

function buildHeaderBlock(title: string, data: LogicData): Content[] {
	return [
		{ columns: [{ image: LOGO_BASE64, width: 108 }, { stack: [{ text: COMPANY_NAME, style: 'companyName', alignment: 'right' }, { text: title, style: 'reportTitle', alignment: 'right', margin: [0, 4, 0, 0] }, { text: `${data.project.name} / ${data.unit.name}`, style: 'reportMeta', alignment: 'right', margin: [0, 4, 0, 0] }] }] },
		{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 760, y2: 0, lineWidth: 1, lineColor: COLORS.teal }], margin: [0, 12, 0, 12] },
		{ columns: [{ text: `Generated ${formatDate()}`, style: 'smallMeta' }, { text: `User ${localStorage.getItem('username') || 'N/A'}`, style: 'smallMeta', alignment: 'right' }], margin: [0, 0, 0, 12] },
		{ columns: [{ text: 'KP = Keypad', style: 'legendText' }, { text: 'B = Button', style: 'legendText' }, { text: 'RCU / Ch = RCU output channel', style: 'legendText' }, { text: 'DIN / Ch = DIN module output channel', style: 'legendText' }], columnGap: 12, margin: [0, 0, 0, 12] },
		{ table: { widths: ['*', '*', '*', '*'], body: [[{ text: 'Zones', style: 'summaryHead' }, { text: 'Rooms', style: 'summaryHead' }, { text: 'Loads', style: 'summaryHead' }, { text: 'Keypads', style: 'summaryHead' }], [String(data.zones.length), String(data.rooms.length), String(data.loads.length), String(data.keypads.length)]] }, layout: { hLineColor: () => COLORS.line, vLineColor: () => COLORS.line, fillColor: (rowIndex: number) => (rowIndex === 0 ? COLORS.paper : null) }, margin: [0, 0, 0, 18] as [number, number, number, number] }
	];
}

function buildAreaSection(title: string, tableHeaders: string[], rows: string[][], widths: Array<string | number>): Content[] {
	return [
		{ table: { widths: ['*'], body: [[{ text: title, style: 'roomTitleBand' }]] }, layout: { hLineWidth: () => 0, vLineWidth: () => 0, fillColor: () => COLORS.paper }, margin: [0, 2, 0, 6] as [number, number, number, number] },
		{ table: { headerRows: 1, widths, body: [[...tableHeaders.map((header) => ({ text: header, style: 'tableHead' }))], ...rows] }, layout: { hLineColor: () => COLORS.line, vLineColor: () => COLORS.line, fillColor: (rowIndex: number) => (rowIndex === 0 ? COLORS.paper : null) }, margin: [0, 0, 0, 14] as [number, number, number, number] }
	];
}

function baseDocDefinition(title: string, content: Content[], unitName: string): TDocumentDefinitions {
	return {
		pageSize: 'A4',
		pageOrientation: 'landscape',
		pageMargins: [28, 26, 28, 28],
		footer: (currentPage: number, pageCount: number) => ({
			columns: [{ text: `${COMPANY_NAME} | ${unitName}`, style: 'footerText', margin: [28, 8, 0, 0] }, { text: `${title} | Page ${currentPage} of ${pageCount}`, style: 'footerText', alignment: 'right', margin: [0, 8, 28, 0] }]
		}),
		content,
		defaultStyle: { fontSize: 8, color: COLORS.ink },
		styles: { companyName: { fontSize: 9, bold: true, color: COLORS.ink }, reportTitle: { fontSize: 20, bold: true, color: COLORS.ink }, reportMeta: { fontSize: 10, color: COLORS.muted }, smallMeta: { fontSize: 8, color: COLORS.muted }, legendText: { fontSize: 7, color: COLORS.teal }, summaryHead: { fontSize: 8, bold: true, color: COLORS.ink }, roomTitleBand: { fontSize: 10, bold: true, color: COLORS.ink, margin: [8, 6, 8, 6] }, sectionTitle: { fontSize: 12, bold: true, color: COLORS.ink, margin: [0, 8, 0, 8] }, tableHead: { fontSize: 8, bold: true, color: COLORS.ink }, footerText: { fontSize: 7, color: COLORS.muted } }
	};
}

export async function generateUnitLoadChartPdf(data: LogicData, filename = 'load_chart.pdf') {
	const pdfMake = await loadPdfMake();
	const maps = createMaps(data);
	const sections = buildLoadChartSections(data, maps);
	const content: Content[] = [
		...buildHeaderBlock('Load Chart', data),
		{ text: 'Area-first load schedule showing each load, its assigned panel/channel, and the buttons that operate it.', style: 'smallMeta', margin: [0, 0, 0, 10] as [number, number, number, number] }
	];
	for (const section of sections) content.push(...buildAreaSection(getRoomTitle(section.room, maps), ['Sl No', 'Load', 'Load Type', 'Panel / Channel', 'Controlled By'], section.rows, [34, 140, 72, 110, '*']));
	pdfMake.createPdf(baseDocDefinition('Load Chart', content, data.unit.name)).download(filename);
}

export async function generateUnitLogicSheetPdf(data: LogicData, filename = 'logic_sheet.pdf') {
	const pdfMake = await loadPdfMake();
	const maps = createMaps(data);
	const logicSections = buildLogicSections(data, maps);
	const ledRows = buildLedLogicRows(data, maps);
	const content: Content[] = [
		...buildHeaderBlock('Logic Sheet', data),
		{ text: 'Simple control logic by area: which button changes state, what it controls, and where that load lands in the panel.', style: 'smallMeta', margin: [0, 0, 0, 10] as [number, number, number, number] }
	];
	for (const section of logicSections) content.push(...buildAreaSection(getRoomTitle(section.room, maps), ['Sl No', 'Keypad / Button', 'State', 'Controls', 'Type', 'Panel / Channel'], section.rows, [34, 150, 46, '*', 64, 98]));
	if (ledRows.length) {
		content.push(
			{ text: 'Saved LED Logic', style: 'sectionTitle', pageBreak: logicSections.length ? 'before' : undefined },
			{ text: 'Only saved LED mappings are shown here. These are the LED relationships included in export.', style: 'smallMeta', margin: [0, 0, 0, 8] },
			{ table: { headerRows: 1, widths: [34, 150, 44, 150, '*'], body: [['Sl No', 'When This Button Turns', 'State', 'Controls', 'Set These LEDs'].map((header) => ({ text: header, style: 'tableHead' })), ...ledRows] }, layout: { hLineColor: () => COLORS.line, vLineColor: () => COLORS.line, fillColor: (rowIndex: number) => (rowIndex === 0 ? COLORS.paper : null) } }
		);
	}
	pdfMake.createPdf(baseDocDefinition('Logic Sheet', content, data.unit.name)).download(filename);
}
